import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PieChart, TrendingUp, TrendingDown, Calendar, DollarSign, Users, BarChart3, FileText, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import financeService from '../services/financeService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import { Link } from 'react-router-dom';

const getMonthName = (month) => {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[month - 1] || '';
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }
  return years;
};

const MetricCard = ({ icon: Icon, label, value, trend, variant = 'default', href }) => {
  const variantStyles = {
    default: 'bg-white border-gray-100 hover:border-primary-200',
    primary: 'bg-gradient-to-br from-primary-500 to-primary-600 border-primary-400',
    danger: 'bg-gradient-to-br from-red-500 to-red-600 border-red-400',
    success: 'bg-gradient-to-br from-green-500 to-green-600 border-green-400'
  };

  const textStyles = {
    default: 'text-gray-900',
    primary: 'text-white',
    danger: 'text-white',
    success: 'text-white'
  };

  const subTextStyles = {
    default: 'text-gray-500',
    primary: 'text-primary-100',
    danger: 'text-red-100',
    success: 'text-green-100'
  };

  const content = (
    <div className={`group relative rounded-2xl border-2 p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${variantStyles[variant]}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={`text-sm font-medium ${subTextStyles[variant]} mb-1`}>{label}</p>
          <p className={`text-2xl font-bold tracking-tight ${textStyles[variant]}`}>
            {value}
          </p>
          {trend && (
            <p className={`text-xs mt-1 ${subTextStyles[variant]} opacity-80`}>{trend}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl transition-colors ${variant === 'default' ? 'bg-gray-50 group-hover:bg-primary-50' : 'bg-white/20 group-hover:bg-white/30'}`}>
          <Icon className={`w-6 h-6 ${variant === 'default' ? 'text-gray-400 group-hover:text-primary-500' : 'text-white'}`} />
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="block">
        {content}
      </Link>
    );
  }

  return content;
};

const FinanceDashboard = () => {
  const { user } = useAuth();
  const canView = hasPermission(user?.role, 'financial:view') || hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:expenses');

  const [filters, setFilters] = useState({ month: '', year: '', forecastMonths: '' });
  const [activeView, setActiveView] = useState('realized');

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const handlePreviousMonth = () => {
    let newMonth = filters.month ? parseInt(filters.month) : currentMonth;
    let newYear = filters.year ? parseInt(filters.year) : currentYear;
    if (newMonth === 1) { newMonth = 12; newYear -= 1; } else { newMonth -= 1; }
    setFilters({ ...filters, month: newMonth.toString(), year: newYear.toString() });
  };

  const handleNextMonth = () => {
    let newMonth = filters.month ? parseInt(filters.month) : currentMonth;
    let newYear = filters.year ? parseInt(filters.year) : currentYear;
    if (newMonth === 12) { newMonth = 1; newYear += 1; } else { newMonth += 1; }
    setFilters({ ...filters, month: newMonth.toString(), year: newYear.toString() });
  };

  const handleCurrentMonth = () => {
    setFilters({ month: currentMonth.toString(), year: currentYear.toString() });
  };

  const displayMonth = filters.month ? parseInt(filters.month) : currentMonth;
  const displayYear = filters.year ? parseInt(filters.year) : currentYear;

  const { data, isLoading } = useQuery({
    queryKey: ['finance-stats', filters],
    queryFn: () => {
      const params = {};
      if (filters.month && filters.month !== '') params.month = filters.month;
      if (filters.year && filters.year !== '') params.year = filters.year;
      if (filters.forecastMonths && filters.forecastMonths !== '') params.forecastMonths = filters.forecastMonths;
      return financeService.getStats(params);
    },
    staleTime: 0,
    refetchOnWindowFocus: true
  });

  const stats = data?.data || {
    totals: { incomes: 0, expenses: 0, balance: 0, incomesForecast: 0, expensesForecast: 0, balanceForecast: 0 },
    indicators: { realProfitPct: 0 },
    topExpenseCategories: [],
    paymentDistribution: [],
    taxes: 0
  };

  const { data: forecastData } = useQuery({
    queryKey: ['finance-forecast', { months_ahead: 6 }],
    queryFn: () => financeService.getForecast({ months_ahead: 6 })
  });
  const forecast = forecastData?.data?.forecast || [];
  const costDrivers = forecastData?.data?.costDrivers || [];

  const { data: expensesData } = useQuery({
    queryKey: ['finance-expenses-dashboard', { page: 1, limit: 1000 }],
    queryFn: () => financeService.getExpenses({ page: 1, limit: 1000 }),
    staleTime: 30 * 1000,
  });

  const { payrollTotal, otherExpensesTotal } = useMemo(() => {
    if (stats.totals.payroll !== undefined && stats.totals.otherExpenses !== undefined) {
      return { payrollTotal: stats.totals.payroll || 0, otherExpensesTotal: stats.totals.otherExpenses || 0 };
    }
    if (!expensesData?.data?.data) return { payrollTotal: 0, otherExpensesTotal: 0 };
    const items = expensesData.data.data;
    const payroll = items.filter(item => item.source_type === 'payslip');
    const others = items.filter(item => item.source_type !== 'payslip');
    return {
      payrollTotal: payroll.reduce((sum, item) => sum + (parseFloat(item.net_amount) || 0), 0),
      otherExpensesTotal: others.reduce((sum, item) => sum + (parseFloat(item.net_amount) || 0), 0)
    };
  }, [expensesData, stats.totals]);

  const { data: forecastedExpensesData } = useQuery({
    queryKey: ['finance-forecasted-expenses-dashboard', filters.month || currentMonth, filters.year || currentYear],
    queryFn: () => financeService.getForecastedExpenses({ month: filters.month || currentMonth, year: filters.year || currentYear }),
    staleTime: 30 * 1000,
  });

  const forecastedPayroll = forecastedExpensesData?.data?.totals?.payroll || 0;

  const balanceVariant = stats.totals.balance >= 0 ? 'success' : 'danger';
  const forecastBalanceVariant = stats.totals.balanceForecast >= 0 ? 'success' : 'danger';

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-red-800">Você não tem permissão para visualizar o módulo financeiro.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-gray-500 mt-1">Visão geral do financeiro</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/financeiro" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Dashboard</Link>
          <Link to="/financeiro/entradas" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Entradas</Link>
          <Link to="/financeiro/saidas" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Saídas</Link>
          <Link to="/financeiro/folha" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Folha</Link>
          <Link to="/financeiro/categorias" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Categorias</Link>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-2 flex items-center gap-1">
          <button
            onClick={handlePreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <select
            value={filters.month || currentMonth}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            className="px-2 py-1.5 bg-transparent text-gray-700 font-medium text-sm focus:outline-none"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </select>
          <select
            value={filters.year || currentYear}
            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="px-2 py-1.5 bg-transparent text-gray-700 font-medium text-sm focus:outline-none"
          >
            {getYearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <button
          onClick={handleCurrentMonth}
          className="px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl font-medium transition-colors text-sm border-2 border-primary-200"
        >
          Mês Atual
        </button>

        <select
          value={filters.forecastMonths}
          onChange={(e) => setFilters({ ...filters, forecastMonths: e.target.value })}
          className="px-4 py-2 border-2 border-gray-200 rounded-xl text-gray-700 text-sm focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
        >
          <option value="">Previsão: mês atual</option>
          <option value="3">Previsão: 3 meses</option>
          <option value="6">Previsão: 6 meses</option>
          <option value="9">Previsão: 9 meses</option>
          <option value="12">Previsão: 12 meses</option>
        </select>

        <span className="ml-auto text-sm text-gray-500 font-medium">
          {getMonthName(displayMonth)} {displayYear}
        </span>
      </div>

      {/* Hero Balance Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-2 border-gray-700 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm font-medium mb-2">Saldo do Período</p>
            <p className={`text-5xl font-bold tracking-tight ${stats.totals.balance >= 0 ? 'text-white' : 'text-red-400'}`}>
              R$ {stats.totals.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm font-medium">
                  R$ {stats.totals.incomes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} entradas
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm font-medium">
                  R$ {stats.totals.expenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} saídas
                </span>
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="flex gap-3">
              <Link
                to="/financeiro/entradas"
                className="px-5 py-3 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-medium text-sm transition-colors flex items-center gap-2"
              >
                Entradas <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/financeiro/saidas"
                className="px-5 py-3 bg-primary-500 hover:bg-primary-600 rounded-2xl text-white font-medium text-sm transition-colors flex items-center gap-2"
              >
                Saídas <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          icon={TrendingUp}
          label="Entradas"
          value={`R$ ${stats.totals.incomes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          href="/financeiro/entradas"
        />
        <MetricCard
          icon={TrendingDown}
          label="Saídas"
          value={`R$ ${stats.totals.expenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          href="/financeiro/saidas"
        />
        <MetricCard
          icon={Users}
          label="Folha"
          value={`R$ ${payrollTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          href="/financeiro/folha"
        />
        <MetricCard
          icon={DollarSign}
          label="Impostos"
          value={`R$ ${parseFloat(stats.taxes).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
        />
      </div>

      {/* Realized vs Forecast Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Realized */}
        <div className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-primary-50 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              Realizado
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Folha de Pagamento</span>
              </div>
              <span className="font-bold text-gray-900">R$ {payrollTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Outras Despesas</span>
              </div>
              <span className="font-bold text-gray-900">R$ {otherExpensesTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-primary-50 rounded-xl border-2 border-primary-100">
              <span className="text-primary-700 font-semibold">Total</span>
              <span className="font-bold text-primary-700">R$ {stats.totals.expenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        {/* Forecast */}
        <div className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-gray-600" />
              Previsto
              {filters.forecastMonths && (
                <span className="text-xs text-gray-500 font-normal">({filters.forecastMonths} meses)</span>
              )}
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Folha Prevista</span>
              </div>
              <span className="font-bold text-gray-900">R$ {Number(forecastedPayroll).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Outras Previstas</span>
              </div>
              <span className="font-bold text-gray-900">R$ {Number((stats.totals.expensesForecast || 0) - forecastedPayroll).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${forecastBalanceVariant === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <span className={`font-semibold ${forecastBalanceVariant === 'success' ? 'text-green-700' : 'text-red-700'}`}>Saldo Previsto</span>
              <span className={`font-bold ${forecastBalanceVariant === 'success' ? 'text-green-700' : 'text-red-700'}`}>R$ {Number(stats.totals.balanceForecast).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Forecast Chart */}
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-500" />
            Forecast (6 meses)
          </h3>
          {forecast.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-400">
              <p>Sem dados suficientes para previsão.</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Entradas</span>
                  <span className="text-green-600 font-medium">R$ {Math.max(...forecast.map(f => f.expectedIncome || f.income || 0)).toLocaleString('pt-BR')}</span>
                </div>
                <div className="h-16 flex items-end gap-1">
                  {forecast.map((f, i) => {
                    const max = Math.max(...forecast.map(f => f.expectedIncome || f.income || 0), 1);
                    const val = f.expectedIncome || f.income || 0;
                    const height = (val / max) * 100;
                    return (
                      <div key={i} className="flex-1 bg-gradient-to-t from-green-500 to-green-400 rounded-t transition-all hover:opacity-80" style={{ height: `${Math.max(height, 4)}%` }} />
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Saídas</span>
                  <span className="text-red-600 font-medium">R$ {Math.max(...forecast.map(f => f.expectedExpense || f.expense || 0)).toLocaleString('pt-BR')}</span>
                </div>
                <div className="h-16 flex items-end gap-1">
                  {forecast.map((f, i) => {
                    const max = Math.max(...forecast.map(f => f.expectedExpense || f.expense || 0), 1);
                    const val = f.expectedExpense || f.expense || 0;
                    const height = (val / max) * 100;
                    return (
                      <div key={i} className="flex-1 bg-gradient-to-t from-red-500 to-red-400 rounded-t transition-all hover:opacity-80" style={{ height: `${Math.max(height, 4)}%` }} />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Distribution */}
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary-500" />
            Distribuição de Entradas
          </h3>
          {stats.paymentDistribution.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-400">
              <p>Sem dados</p>
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <svg viewBox="0 0 100 100" className="w-32 h-32 flex-shrink-0">
                {(() => {
                  const values = stats.paymentDistribution.map(p => Number(p.total));
                  const total = values.reduce((a, b) => a + b, 0) || 1;
                  let acc = 0;
                  const colors = ['#49943d', '#67ab67', '#8dc08d', '#b3d5b3', '#d9ead9', '#f0f7f0'];
                  return values.map((v, i) => {
                    const start = (acc / total) * 2 * Math.PI - Math.PI / 2;
                    acc += v;
                    const end = (acc / total) * 2 * Math.PI - Math.PI / 2;
                    const large = end - start > Math.PI ? 1 : 0;
                    const r = 40, cx = 50, cy = 50;
                    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
                    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
                    const d = v === total ? `M ${cx} ${cy} m -${r} 0 a ${r} ${r} 0 1 1 ${r * 2} 0 a ${r} ${r} 0 1 1 -${r * 2} 0` : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
                    return <path key={i} d={d} fill={colors[i % colors.length]} stroke="white" strokeWidth="1" />;
                  });
                })()}
              </svg>
              <div className="flex-1 space-y-2">
                {stats.paymentDistribution.map((p, i) => (
                  <div key={p.method} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: ['#49943d', '#67ab67', '#8dc08d', '#b3d5b3', '#d9ead9', '#f0f7f0'][i % 6] }} />
                      <span className="text-sm text-gray-700">{p.method}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">R$ {Number(p.total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Indicators */}
      <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Indicadores</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-xl text-center">
            <p className="text-sm text-gray-500 mb-1">% Lucro Real</p>
            <p className="text-2xl font-bold text-primary-600">{stats.indicators.realProfitPct.toFixed(1)}%</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl text-center">
            <p className="text-sm text-gray-500 mb-1">Impostos</p>
            <p className="text-2xl font-bold text-gray-900">R$ {parseFloat(stats.taxes).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl text-center">
            <p className="text-sm text-gray-500 mb-1">% Folha/Entradas</p>
            <p className="text-2xl font-bold text-gray-900">
              {stats.totals.incomes > 0 ? ((payrollTotal / stats.totals.incomes) * 100).toFixed(1) : '0.0'}%
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl text-center">
            <p className="text-sm text-gray-500 mb-1">% Folha/Saídas</p>
            <p className="text-2xl font-bold text-gray-900">
              {stats.totals.expenses > 0 ? ((payrollTotal / stats.totals.expenses) * 100).toFixed(1) : '0.0'}%
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {hasPermission(user?.role, 'financial:full') && (
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/financeiro/folha/analise"
              className="group flex items-center gap-4 p-5 bg-primary-50 hover:bg-primary-100 rounded-2xl border-2 border-primary-100 hover:border-primary-200 transition-all"
            >
              <div className="p-3 bg-primary-100 group-hover:bg-primary-200 rounded-xl transition-colors">
                <BarChart3 className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Análise Detalhada</p>
                <p className="text-sm text-gray-500">Comparativo previsto vs efetivo</p>
              </div>
            </Link>
            <Link
              to="/financeiro/saidas-previstas"
              className="group flex items-center gap-4 p-5 bg-gray-50 hover:bg-orange-50 rounded-2xl border-2 border-gray-100 hover:border-orange-200 transition-all"
            >
              <div className="p-3 bg-gray-100 group-hover:bg-orange-100 rounded-xl transition-colors">
                <TrendingDown className="w-6 h-6 text-gray-600 group-hover:text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Saídas Previstas</p>
                <p className="text-sm text-gray-500">Previsão de gastos</p>
              </div>
            </Link>
            <Link
              to="/financeiro/folha"
              className="group flex items-center gap-4 p-5 bg-gray-50 hover:bg-gray-100 rounded-2xl border-2 border-gray-100 hover:border-gray-200 transition-all"
            >
              <div className="p-3 bg-gray-100 group-hover:bg-gray-200 rounded-xl transition-colors">
                <FileText className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Gestão de Holerites</p>
                <p className="text-sm text-gray-500">Criar e gerenciar</p>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Top Categories & Cost Drivers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Categorias de Despesa</h3>
          {stats.topExpenseCategories.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Sem dados</p>
          ) : (
            <div className="space-y-3">
              {stats.topExpenseCategories.slice(0, 5).map((c) => (
                <div key={c.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{c.category || 'Sem categoria'}</span>
                    <span className="font-medium text-gray-900">R$ {Number(c.total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full transition-all"
                      style={{ width: `${(Number(c.total) / Math.max(...stats.topExpenseCategories.map(i => Number(i.total)), 1)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Maiores Custos (90 dias)</h3>
          {costDrivers.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Sem dados</p>
          ) : (
            <div className="space-y-2">
              {costDrivers.slice(0, 5).map((c, i) => (
                <div key={c.category} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-gray-700 font-medium">{c.category || 'Sem categoria'}</span>
                  </div>
                  <span className="font-bold text-primary-600">R$ {parseFloat(c.total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;